// File: src/components/MyRCMEventPicker.js
// Browse MyRCM's own event list inside the import dialog: pick an event, then
// pick a class. Produces the same base URL that used to be pasted by hand,
// so everything downstream of it is unchanged.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, ArrowLeft, MapPin, Calendar, Building2, AlertCircle, Tag } from 'lucide-react';
import { API_BASE_URL } from '../App';
import {
  MYRCM_PAGE_SIZE,
  MYRCM_COUNTRY_IDS,
  buildMyRCMListUrl,
  buildMyRCMEventReportUrl,
  buildMyRCMClassUrl,
  parseMyRCMEventList,
  parseMyRCMClassList,
  parseMyRCMCountryOptions
} from '../utils/utils';
import { useLanguage } from '../contexts/LanguageContext';
import { useCountry } from '../contexts/CountryContext';
import { handleUnauthorized } from '../utils/session';

// Fetch a myrcm.ch page through the backend proxy and hand back a parsed
// Document. Throws on transport/auth failures so callers can show one message.
const fetchMyRCMDocument = async (targetUrl, token) => {
  const response = await fetch(
    `${API_BASE_URL}/proxy/myrcm?url=${encodeURIComponent(targetUrl)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (handleUnauthorized(response)) {
    // The session handler already alerted and logged out; stop quietly.
    return null;
  }

  if (!response.ok) {
    throw new Error(`MyRCM request failed (${response.status})`);
  }

  const html = await response.text();
  return new DOMParser().parseFromString(html, 'text/html');
};

function MyRCMEventPicker({ token, onSelect, onRequestPasteMode, disabled }) {
  const { t } = useLanguage();
  const { currentCountry, countries } = useCountry();

  // Step 1 - event list
  const [scope, setScope] = useState('archive');
  const [countryId, setCountryId] = useState(
    () => String(MYRCM_COUNTRY_IDS[currentCountry.code] || '')
  );
  // Seed the dropdown from the countries the app already knows, so the
  // preselected country is a real option from the first render. Without this the
  // select falls back to "all countries" while the request is actually filtered,
  // which misreports the filter that is in effect.
  const [countryOptions, setCountryOptions] = useState(() =>
    countries
      .filter((country) => MYRCM_COUNTRY_IDS[country.code])
      .map((country) => ({ id: String(MYRCM_COUNTRY_IDS[country.code]), name: country.name }))
  );
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  // '' | 'fetch' (couldn't reach MyRCM) | 'markup' (page didn't parse)
  const [listError, setListError] = useState('');

  // Step 2 - class list for the chosen event
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState('');

  // Filter changes can outrun each other; only the newest response may land.
  const listRequestId = useRef(0);

  useEffect(() => {
    const requestId = ++listRequestId.current;
    let cancelled = false;

    const loadEvents = async () => {
      setListLoading(true);
      setListError('');

      try {
        const doc = await fetchMyRCMDocument(
          buildMyRCMListUrl({ scope, countryId, search: appliedSearch, page }),
          token
        );
        if (cancelled || requestId !== listRequestId.current || !doc) return;

        const parsed = parseMyRCMEventList(doc);
        if (parsed === null) {
          setEvents([]);
          setTotal(0);
          setListError('markup');
          return;
        }

        setEvents(parsed.events);
        setTotal(parsed.total);

        // MyRCM ships its whole country list on every list page; once we have
        // it, it replaces the seeded subset so every MyRCM country is reachable.
        const allCountries = parseMyRCMCountryOptions(doc);
        if (allCountries.length) setCountryOptions(allCountries);
      } catch (error) {
        if (cancelled || requestId !== listRequestId.current) return;
        console.error('MyRCM event list failed:', error);
        setEvents([]);
        setTotal(0);
        setListError('fetch');
      } finally {
        if (!cancelled && requestId === listRequestId.current) {
          setListLoading(false);
        }
      }
    };

    loadEvents();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, countryId, appliedSearch, page, token]);

  const openEvent = useCallback(async (event) => {
    setSelectedEvent(event);
    setClasses([]);
    setClassesError('');
    setClassesLoading(true);

    try {
      const doc = await fetchMyRCMDocument(buildMyRCMEventReportUrl(event.id), token);
      if (!doc) return;

      const parsed = parseMyRCMClassList(doc);
      if (parsed === null) {
        setClassesError('markup');
        return;
      }
      setClasses(parsed);
    } catch (error) {
      console.error('MyRCM class list failed:', error);
      setClassesError('fetch');
    } finally {
      setClassesLoading(false);
    }
  }, [token]);

  const changeScope = (nextScope) => {
    setScope(nextScope);
    setPage(0);
  };

  const changeCountry = (nextCountryId) => {
    setCountryId(nextCountryId);
    setPage(0);
  };

  // Search on submit only - never per keystroke, so we don't hammer myrcm.ch.
  const applySearch = (submitEvent) => {
    submitEvent.preventDefault();
    setAppliedSearch(searchInput);
    setPage(0);
  };

  // A parse failure means MyRCM's markup moved under us, which is a different
  // problem from not reaching it at all - and the useful answer to the first one
  // is "fall back to pasting a URL", so offer that right where the error shows.
  const renderError = (kind, fetchFailedKey) => (
    <div className="py-4 space-y-2">
      <p className="flex items-center gap-2 text-red-700 dark:text-red-400">
        <AlertCircle className="w-4 h-4 shrink-0" />
        {kind === 'markup' ? t('myrcmSiteChanged') : t(fetchFailedKey)}
      </p>
      {onRequestPasteMode && (
        <button
          type="button"
          onClick={onRequestPasteMode}
          className="text-ink-muted underline hover:text-ink"
        >
          {t('myrcmUsePasteInstead')}
        </button>
      )}
    </div>
  );

  const lastPageReached = (page + 1) * MYRCM_PAGE_SIZE >= total;
  const rangeFrom = total === 0 ? 0 : page * MYRCM_PAGE_SIZE + 1;
  const rangeTo = Math.min((page + 1) * MYRCM_PAGE_SIZE, total);

  // ---- Step 2: class list -------------------------------------------------
  if (selectedEvent) {
    return (
      <div className="border border-line rounded bg-paper-alt">
        <div className="flex items-center gap-3 p-3 border-b border-line">
          <button
            type="button"
            onClick={() => setSelectedEvent(null)}
            className="flex items-center gap-1 text-ink-muted hover:text-ink"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('myrcmBackToEvents')}
          </button>
          <span className="text-ink font-semibold truncate">{selectedEvent.name}</span>
        </div>

        <div className="p-3">
          <p className="text-ink-muted mb-2 font-semibold">{t('myrcmSelectClass')}</p>

          {classesLoading && (
            <p className="text-ink-muted py-4 text-center">{t('myrcmLoadingClasses')}</p>
          )}

          {!classesLoading && classesError &&
            renderError(classesError, 'myrcmFailedToLoadClasses')}

          {!classesLoading && !classesError && classes.length === 0 && (
            <p className="text-ink-muted py-4 text-center">{t('myrcmNoClassesForEvent')}</p>
          )}

          {!classesLoading && !classesError && classes.length > 0 && (
            <div className="grid gap-2 sm:grid-cols-2">
              {classes.map((raceClass) => (
                <button
                  key={raceClass.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(
                    buildMyRCMClassUrl(selectedEvent.id, raceClass.id),
                    { eventName: selectedEvent.name, className: raceClass.name }
                  )}
                  className="flex items-center gap-2 px-4 py-3 bg-paper border border-line rounded text-ink hover:border-accent text-left"
                >
                  <Tag className="w-4 h-4 text-ink-muted shrink-0" />
                  <span className="truncate">{raceClass.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- Step 1: event list -------------------------------------------------
  return (
    <div className="border border-line rounded bg-paper-alt">
      <div className="p-3 border-b border-line space-y-3">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => changeScope('archive')}
            className={`px-4 py-2 rounded font-semibold ${
              scope === 'archive' ? 'bg-accent text-accent-ink' : 'bg-paper text-ink-muted border border-line'
            }`}
          >
            {t('myrcmScopeArchive')}
          </button>
          <button
            type="button"
            onClick={() => changeScope('live')}
            className={`px-4 py-2 rounded font-semibold ${
              scope === 'live' ? 'bg-accent text-accent-ink' : 'bg-paper text-ink-muted border border-line'
            }`}
          >
            {t('myrcmScopeLive')}
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={countryId}
            onChange={(e) => changeCountry(e.target.value)}
            aria-label={t('myrcmFilterCountry')}
            className="px-3 py-2 bg-paper border border-line rounded text-ink sm:w-56"
          >
            <option value="">{t('myrcmAllCountries')}</option>
            {countryOptions.map((country) => (
              <option key={country.id} value={country.id}>{country.name}</option>
            ))}
          </select>

          <form onSubmit={applySearch} className="flex gap-2 flex-1">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('myrcmSearchEventsPlaceholder')}
              className="flex-1 px-3 py-2 bg-paper border border-line rounded text-ink"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-paper border border-line rounded text-ink-muted hover:text-ink flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              {t('myrcmSearch')}
            </button>
          </form>
        </div>
      </div>

      <div className="p-3">
        {listLoading && (
          <p className="text-ink-muted py-6 text-center">{t('myrcmLoadingEvents')}</p>
        )}

        {!listLoading && listError &&
          renderError(listError, 'myrcmFailedToLoadEvents')}

        {!listLoading && !listError && events.length === 0 && (
          <p className="text-ink-muted py-6 text-center">{t('myrcmNoEventsFound')}</p>
        )}

        {!listLoading && !listError && events.length > 0 && (
          <ul className="space-y-2 max-h-80 overflow-y-auto">
            {events.map((event) => (
              <li key={event.id}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => openEvent(event)}
                  className={`w-full text-left px-4 py-3 bg-paper border border-line rounded hover:border-accent ${
                    event.hasReports ? '' : 'opacity-60'
                  }`}
                >
                  <span className="block text-ink font-semibold">{event.name}</span>
                  <span className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-muted mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {event.dateTo ? `${event.dateFrom} – ${event.dateTo}` : event.dateFrom}
                    </span>
                    {event.organizer && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {event.organizer}
                      </span>
                    )}
                    {event.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {event.location}
                        {event.countryCode ? ` (${event.countryCode})` : ''}
                      </span>
                    )}
                    {!event.hasReports && (
                      <span className="text-ink-muted">{t('myrcmNoReportsBadge')}</span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {!listError && total > 0 && (
        <div className="flex items-center justify-between p-3 border-t border-line">
          <span className="text-sm text-ink-muted">
            {t('myrcmResults')} {rangeFrom}–{rangeTo} / {total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              disabled={page === 0 || listLoading}
              className="px-3 py-1 bg-paper border border-line rounded text-ink-muted hover:text-ink disabled:opacity-40 flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              {t('myrcmPreviousPage')}
            </button>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={lastPageReached || listLoading}
              className="px-3 py-1 bg-paper border border-line rounded text-ink-muted hover:text-ink disabled:opacity-40 flex items-center gap-1"
            >
              {t('myrcmNextPage')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyRCMEventPicker;
